import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import { Ayuda } from "../context/AyudaContext";

const formaVacia = { nombre: "", porcentaje: "" };

// Catálogo de retenciones (IR, IMI, las que haga falta). Se marcan por proveedor en Proveedores y Pagos
// descuenta cada una del monto a pagar. Desactivar una retención no cambia los pagos ya registrados.
function GestionarRetenciones() {
  const [retenciones, setRetenciones] = useState([]);
  const [form, setForm] = useState(formaVacia);
  const [editandoId, setEditandoId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  const cargar = () =>
    apiFetch("/api/retenciones")
      .then((data) => setRetenciones(data || []))
      .catch((err) => setError(err.message));

  useEffect(() => {
    cargar();
  }, []);

  const campo = (nombre, valor) => setForm((prev) => ({ ...prev, [nombre]: valor }));

  const cancelar = () => {
    setEditandoId(null);
    setForm(formaVacia);
  };

  const editar = (r) => {
    setMensaje("");
    setError("");
    setEditandoId(r.id);
    setForm({ nombre: r.nombre, porcentaje: String(r.porcentaje), activa: r.activa });
  };

  const guardar = async (e) => {
    e.preventDefault();
    setMensaje("");
    setError("");
    if (!form.nombre.trim()) return setError("Escribe el nombre de la retención.");
    const porcentaje = Number(form.porcentaje);
    if (form.porcentaje === "" || !Number.isFinite(porcentaje) || porcentaje <= 0 || porcentaje > 100) {
      return setError("El porcentaje debe ser mayor que 0 y no pasar de 100.");
    }
    setGuardando(true);
    try {
      const r = await apiFetch(editandoId ? `/api/retenciones/${editandoId}` : "/api/retenciones", {
        method: "POST",
        body: JSON.stringify({ ...form, porcentaje }),
      });
      setMensaje(r?.message || "Retención guardada.");
      cancelar();
      await cargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  // Activar / desactivar desde la lista, sin tocar nombre ni porcentaje.
  const alternarActiva = async (r) => {
    setMensaje("");
    setError("");
    try {
      await apiFetch(`/api/retenciones/${r.id}`, {
        method: "POST",
        body: JSON.stringify({ nombre: r.nombre, porcentaje: r.porcentaje, activa: !r.activa }),
      });
      setMensaje(r.activa ? `${r.nombre} desactivada: ya no se descuenta en pagos nuevos.` : `${r.nombre} activada.`);
      await cargar();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={estilos.tarjeta}>
      <h3 style={{ margin: "0 0 0.3rem", color: "var(--tema-titulo, #0b3c60)" }}>Retenciones</h3>
      <Ayuda>
      <p style={{ margin: "0 0 0.8rem", fontSize: "0.8rem", color: "#5b6472" }}>
        Todas se calculan sobre el subtotal (antes de IVA). Cada retención se marca en el proveedor al que aplica (pantalla
        Proveedores). En Pagos se descuenta del monto y se muestra el neto a pagar. Los cambios aplican a pagos nuevos; los ya registrados conservan lo que se retuvo.
      </p>
      </Ayuda>

      {error && <p style={{ color: "#a3402f" }}>{error}</p>}
      {mensaje && <p style={{ color: "#2f7a54", fontWeight: 600 }}>✓ {mensaje}</p>}

      {retenciones.length === 0 ? (
        <p className="vacio-simple">Todavía no hay retenciones. Agrega la primera abajo.</p>
      ) : (
        retenciones.map((r) => (
          <div key={r.id} style={{ ...estilos.fila, opacity: r.activa ? 1 : 0.55 }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, color: "var(--tema-titulo, #0b3c60)" }}>
                {r.nombre} {!r.activa && <span style={estilos.chipInactiva}>Desactivada</span>}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#5b6472", marginTop: "0.15rem" }}>
                Sobre el subtotal (antes de IVA) · se aplica a {r.proveedores} proveedor{r.proveedores === 1 ? "" : "es"}
              </div>
            </div>
            <div style={estilos.valorBloque}>
              <span style={estilos.valor}>{r.porcentaje}%</span>
              <button type="button" onClick={() => editar(r)} style={estilos.botonSecundario}>
                ✏️ Cambiar
              </button>
              <button type="button" onClick={() => alternarActiva(r)} style={estilos.botonSecundario}>
                {r.activa ? "Desactivar" : "Activar"}
              </button>
            </div>
          </div>
        ))
      )}

      <form onSubmit={guardar} style={estilos.formulario}>
        <div style={{ fontWeight: 700, color: "var(--tema-titulo, #0b3c60)", marginBottom: "0.5rem" }}>
          {editandoId ? "Cambiar retención" : "Agregar otra retención"}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", alignItems: "flex-end" }}>
          <label style={estilos.campo}>
            Nombre
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => campo("nombre", e.target.value)}
              placeholder="Ej. IR 2%, IMI, 1% municipal…"
              style={{ ...estilos.input, width: 220 }}
            />
          </label>
          <label style={estilos.campo}>
            Porcentaje (%)
            <input
              type="number"
              step="any"
              value={form.porcentaje}
              onChange={(e) => campo("porcentaje", e.target.value)}
              style={estilos.input}
            />
          </label>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <button type="submit" disabled={guardando} style={estilos.botonPrincipal}>
              {guardando ? "Guardando..." : editandoId ? "💾 Guardar cambios" : "+ Agregar retención"}
            </button>
            {editandoId && (
              <button type="button" onClick={cancelar} style={estilos.botonSecundario}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

const estilos = {
  tarjeta: { background: "#fff", border: "1px solid #e2e6ea", borderRadius: 14, padding: "1.1rem 1.2rem", marginBottom: "1.2rem", boxShadow: "0 1px 3px rgba(27,42,74,0.06)" },
  fila: { display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "center", justifyContent: "space-between", padding: "0.8rem 0", borderTop: "1px solid #eef1f3" },
  valorBloque: { display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" },
  valor: { fontSize: "1.5rem", fontWeight: 800, color: "var(--tema-titulo, #0b3c60)", fontVariantNumeric: "tabular-nums", minWidth: 70, textAlign: "right" },
  chipInactiva: { fontSize: "0.68rem", fontWeight: 700, color: "#5b6472", background: "#eef1f3", borderRadius: 999, padding: "0.1rem 0.5rem", marginLeft: "0.4rem" },
  formulario: { marginTop: "0.8rem", paddingTop: "0.9rem", borderTop: "1px solid #eef1f3" },
  campo: { display: "flex", flexDirection: "column", gap: "0.2rem", fontSize: "0.78rem", color: "#5b6472", fontWeight: 600 },
  input: { padding: "0.45rem 0.6rem", border: "1px solid #c9d6ea", borderRadius: 8, fontSize: "0.95rem", width: 110 },
  botonPrincipal: { background: "#2f7a54", color: "#fff", border: "none", borderRadius: 8, padding: "0.5rem 0.9rem", fontWeight: 700, cursor: "pointer" },
  botonSecundario: { background: "#fff", color: "var(--tema-titulo, #0b3c60)", border: "1px solid #c9d6ea", borderRadius: 8, padding: "0.45rem 0.8rem", fontWeight: 600, cursor: "pointer" },
};

export default GestionarRetenciones;
